import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Drawer,
  Fab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search as SearchIcon,
  LocationOn as LocationOnIcon,
  Money as MoneyIcon,
  SportsTennis as SportsTennisIcon,
  AccessTime as AccessTimeIcon,
  Clear as ClearIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Lightbulb as LightIcon,
  Home as IndoorIcon,
  Park as OutdoorIcon,
  AddLocation as AddLocationIcon,
  FilterList as FilterIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { NewCourtReportDialog } from '../components/Courts/NewCourtReportDialog';
import { LocationSearchSelect } from '../components/Common/LocationSearchSelect';
import { useAuth } from '../hooks/useAuth';
import { Court, AlgoliaSearchService, AlgoliaSearchResult } from '@matchpoint/shared';
import { initializeAlgoliaForWeb } from '../utils/algoliaInit';

// Styled components for compact filter inputs
const CompactFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-root': {
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 14px',
  },
  '& .MuiInputLabel-outlined': {
    transform: 'translate(14px, 12px) scale(1)',
  },
  '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
    transform: 'translate(14px, -6px) scale(0.75)',
  },
}));

interface LocalFilters {
  search: string;
  city: string;
  country: string;
  facilityType: string;
  courtSurface: string;
  lights: boolean | undefined;
  hasIndoorCourts: boolean;
  courtFees: 'free' | 'paid' | undefined;
}

export const CourtsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signIn, signUp, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [searchResult, setSearchResult] = useState<AlgoliaSearchResult>({
    courts: [],
    totalHits: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
    facets: {
      cities: {},
      countries: {},
      facilityTypes: {},
      courtSurfaces: {},
      courtFees: {}
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [newCourtDialogOpen, setNewCourtDialogOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [filters, setFilters] = useState<LocalFilters>({
    search: searchParams.get('search') || '',
    city: searchParams.get('city') || '',
    country: searchParams.get('country') || '',
    facilityType: searchParams.get('facilityType') || '',
    courtSurface: searchParams.get('courtSurface') || '',
    lights: searchParams.get('lights') === 'true' ? true : searchParams.get('lights') === 'false' ? false : undefined,
    hasIndoorCourts: searchParams.get('hasIndoorCourts') === 'true',
    courtFees: (searchParams.get('courtFees') as LocalFilters['courtFees']) || undefined,
  });

  const algoliaService = useMemo(() => {
    if (!AlgoliaSearchService) {
      console.error('AlgoliaSearchService is undefined!');
      return null;
    }
    
    try {
      return AlgoliaSearchService.getInstance();
    } catch (error) {
      console.error('Error creating AlgoliaSearchService instance:', error);
      return null;
    }
  }, []);

  const searchCourts = async (pageNum: number = 0) => {
    if (!algoliaService) {
      setError('Search service is not available. Please refresh the page.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        query: filters.search,
        page: pageNum,
        hitsPerPage: pageSize,
        filters: buildAlgoliaFilters(),
        facets: ['location.city', 'location.country', 'details.facility_type', 'details.court_surface', 'pricing.category'],
      };

      const result = await algoliaService.searchCourts(searchParams);
      setSearchResult(result);
    } catch (err) {
      console.error('Error searching courts:', err);
      setError('Failed to search courts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const buildAlgoliaFilters = (): string => {
    const filterParts: string[] = [];

    if (filters.city) filterParts.push(`location.city:"${filters.city}"`);
    if (filters.country) filterParts.push(`location.country:"${filters.country}"`);
    if (filters.facilityType) filterParts.push(`details.facility_type:"${filters.facilityType}"`);
    if (filters.courtSurface) filterParts.push(`details.court_surface:"${filters.courtSurface}"`);
    if (filters.lights !== undefined) filterParts.push(`details.lights:${filters.lights}`);
    if (filters.hasIndoorCourts) filterParts.push(`details.indoor_courts > 0`);
    if (filters.courtFees === 'free') filterParts.push(`NOT details.court_fees:"Yes"`);
    else if (filters.courtFees === 'paid') filterParts.push(`details.court_fees:"Yes"`);

    return filterParts.join(' AND ');
  };

useEffect(() => {
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      await initializeAlgoliaForWeb();
      
      // Load filter options first to populate facets
      if (algoliaService) {
        try {
          const filterOptions = await algoliaService.getFilterOptions();
          console.log('Filter options loaded:', filterOptions);
          setSearchResult(prev => ({ ...prev, facets: filterOptions }));
        } catch (err) {
          console.warn('Could not load filter options:', err);
        }
      }
      
      await searchCourts(0);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load courts. Please try again.');
    }
  };

  loadInitialData();
}, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.city) params.set('city', filters.city);
    if (filters.country) params.set('country', filters.country);
    if (filters.facilityType) params.set('facilityType', filters.facilityType);
    if (filters.courtSurface) params.set('courtSurface', filters.courtSurface);
    if (filters.lights !== undefined) params.set('lights', String(filters.lights));
    if (filters.hasIndoorCourts) params.set('hasIndoorCourts', 'true');
    if (filters.courtFees) params.set('courtFees', filters.courtFees);
    
    setSearchParams(params);
  }, [filters, setSearchParams]);

  useEffect(() => {
    if (!loading) {
      setPage(1);
      
      if (filters.search !== searchParams.get('search')) {
        const timeoutId = setTimeout(() => {
          searchCourts(0);
        }, 500);
        return () => clearTimeout(timeoutId);
      } else {
        searchCourts(0);
      }
    }
  }, [filters]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    searchCourts(newPage - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (key: keyof LocalFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      city: '',
      country: '',
      facilityType: '',
      courtSurface: '',
      lights: undefined,
      hasIndoorCourts: false,
      courtFees: undefined,
    });
  };

  const handleCourtClick = (courtId: string) => {
    navigate(`/courts/${courtId}`);
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleSignIn = async (email: string, password: string) => {
    await signIn(email, password);
  };

  const handleSignUp = async (email: string, password: string, name: string) => {
    await signUp(email, password, name);
  };

  const handleRequestNewCourt = () => {
    if (!user) {
      setLoginDialogOpen(true);
      return;
    }
    setNewCourtDialogOpen(true);
  };

  const availableFacilityTypes = Object.keys(searchResult.facets.facilityTypes).sort();
  const availableCourtSurfaces = Object.keys(searchResult.facets.courtSurfaces).sort();

  const activeFiltersCount = [
    filters.city,
    filters.country,
    filters.facilityType,
    filters.courtSurface,
    filters.lights,
    filters.hasIndoorCourts,
    filters.courtFees
  ].filter(f => f).length;

  // Filter Component
  const FilterContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <LocationSearchSelect
        value={filters.city}
        onChange={(value, option) => {
          handleFilterChange('city', value);
          if (option?.country) {
            handleFilterChange('country', option.country);
          }
        }}
        type="city"
        label="City"
        placeholder="Search cities..."
        size="small"
        fullWidth
      />
      
      <LocationSearchSelect
        value={filters.country}
        onChange={(value) => {
          handleFilterChange('country', value);
          if (value !== filters.country && filters.city) {
            handleFilterChange('city', '');
          }
        }}
        type="country"
        label="Country"
        placeholder="Search countries..."
        size="small"
        fullWidth
      />
      
      <CompactFormControl fullWidth size="small">
        <InputLabel>Facility Type</InputLabel>
        <Select
          value={filters.facilityType}
          label="Facility Type"
          onChange={(e) => handleFilterChange('facilityType', e.target.value)}
        >
          <MenuItem value="">All Types</MenuItem>
          {availableFacilityTypes.map(type => (
            <MenuItem key={type} value={type}>
              {type} ({searchResult.facets.facilityTypes[type]})
            </MenuItem>
          ))}
        </Select>
      </CompactFormControl>
      
      <CompactFormControl fullWidth size="small">
        <InputLabel>Court Surface</InputLabel>
        <Select
          value={filters.courtSurface}
          label="Court Surface"
          onChange={(e) => handleFilterChange('courtSurface', e.target.value)}
        >
          <MenuItem value="">All Surfaces</MenuItem>
          {availableCourtSurfaces.map(surface => (
            <MenuItem key={surface} value={surface}>
              {surface} ({searchResult.facets.courtSurfaces[surface]})
            </MenuItem>
          ))}
        </Select>
      </CompactFormControl>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={filters.courtFees === 'free'}
              onChange={(e) => handleFilterChange('courtFees', e.target.checked ? 'free' : undefined)}
              size="small"
            />
          }
          label={<Typography variant="body2">Free Courts Only</Typography>}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={filters.courtFees === 'paid'}
              onChange={(e) => handleFilterChange('courtFees', e.target.checked ? 'paid' : undefined)}
              size="small"
            />
          }
          label={<Typography variant="body2">Paid Courts Only</Typography>}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={filters.lights === true}
              onChange={(e) => handleFilterChange('lights', e.target.checked ? true : undefined)}
              size="small"
            />
          }
          label={<Typography variant="body2">Has Lights</Typography>}
        />
        
        <FormControlLabel
          control={
            <Switch
              checked={filters.hasIndoorCourts}
              onChange={(e) => handleFilterChange('hasIndoorCourts', e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Indoor Courts</Typography>}
        />
      </Box>
    </Box>
  );

  // Court Card Component
  const CourtCard = ({ court }: { court: Court }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[8]
        }
      }}
      onClick={() => handleCourtClick(court.id)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {court.name}
          </Typography>
          {court.pricing?.hourly ? (
            <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              ${court.pricing.hourly}
            </Typography>
          ) : court.details?.court_fees === "Yes" ? (
            <Typography variant="h6" color="primary.main" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Paid
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Free
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {court.location.city || court.location.address}
            {court.location.country && `, ${court.location.country}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
          {court.details?.facility_type && (
            <Chip 
              label={court.details.facility_type}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            />
          )}
          {court.details?.total_courts && court.details.total_courts > 0 && (
            <Chip 
              icon={<OutdoorIcon fontSize="small" />}
              label={`${court.details.total_courts - (court.details.indoor_courts || 0)} outdoor`}
              size="small"
              variant="outlined"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            />
          )}
          {court.details?.court_surface && (
            <Chip 
              label={court.details.court_surface}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            />
          )}
          {court.details?.lights && (
            <Chip 
              icon={<LightIcon fontSize="small" />}
              label="Lights"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            />
          )}
          {court.details?.indoor_courts && court.details.indoor_courts > 0 && (
            <Chip 
              icon={<IndoorIcon fontSize="small" />}
              label={`${court.details.indoor_courts} indoor`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && searchResult.courts.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={60} />
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error && searchResult.courts.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: isMobile ? 8 : 0 }}>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />
      
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: { xs: 2, sm: 4 } }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, mb: 0 }}>
              Tennis Courts
            </Typography>
            <IconButton 
              onClick={() => setFilterDrawerOpen(true)}
              size="small"
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <TuneIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Box>
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Find the perfect court for your next match
            </Typography>
          </Box>
          
          {!isMobile && (
            <Button
              variant="outlined"
              startIcon={<AddLocationIcon />}
              onClick={handleRequestNewCourt}
              color="primary"
            >
              Request New Court
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12}>
            {/* Search Bar */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Search courts"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleFilterChange('search', '')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Results Summary */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                {searchResult.totalHits} courts
                {filters.search && ` for "${filters.search}"`}
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Box>

            {/* Courts Grid */}
            {searchResult.courts.length === 0 && !loading ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No courts found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Try adjusting your search terms or filters.
                </Typography>
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              </Paper>
            ) : (
              <>
                <Grid container spacing={2}>
                  {searchResult.courts.map(court => (
                    <Grid item xs={12} sm={6} lg={isMobile ? 4 : 6} xl={4} key={court.id}>
                      <CourtCard court={court} />
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {searchResult.totalHits > pageSize && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={Math.ceil(searchResult.totalHits / pageSize)}
                      page={page}
                      onChange={(_, newPage) => handlePageChange(newPage)}
                      color="primary"
                      size={isMobile ? "small" : "large"}
                      showFirstButton={!isMobile}
                      showLastButton={!isMobile}
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>

      <Footer />

      {/* Mobile Filter FAB */}
      

      {/* Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            maxHeight: '85vh',
          }
        }}
      >
        <Box sx={{ 
          maxWidth: '400px',
          margin: '0 auto',
          width: '100%',
        }}>
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 1.5,
            paddingBottom: 1,
            padding: 1.5,
            borderBottom: '1px solid #e0e0e0',
          }}>
            <Typography sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: (theme) => theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}>
              <TuneIcon sx={{ fontSize: '1.25rem' }} />
              Filters
            </Typography>
            
            <IconButton 
              onClick={() => setFilterDrawerOpen(false)}
              size="small"
              sx={{ padding: '4px' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Box sx={{ px: 2, pb: 2 }}>
            <FilterContent />
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 0.75,
              mt: 2,
              pt: 2,
              borderTop: '1px solid #e0e0e0',
            }}>
              <Button
                onClick={() => setFilterDrawerOpen(false)}
                variant="contained"
                fullWidth
                sx={{
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  backgroundColor: (theme) => theme.palette.success.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.success.dark,
                  },
                }}
              >
                Apply Filters
              </Button>
              
              <Button
                onClick={() => {
                  handleClearFilters();
                  setFilterDrawerOpen(false);
                }}
                variant="contained"
                fullWidth
                sx={{
                  padding: '8px 12px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  backgroundColor: (theme) => theme.palette.grey[200],
                  color: (theme) => theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[300],
                  },
                }}
              >
                Clear All
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Login Dialog */}
  <LoginDialog
    open={loginDialogOpen}
    onClose={() => setLoginDialogOpen(false)}
  />

      {/* New Court Request Dialog */}
      {user && (
        <NewCourtReportDialog
          open={newCourtDialogOpen}
          onClose={() => setNewCourtDialogOpen(false)}
          user={user}
        />
      )}
    </Box>
  );
};

export default CourtsPage;